import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Compute great-circle arc points between two lat/lng coordinates.
 */
function greatCirclePoints(lat1, lng1, lat2, lng2, n = 60) {
  const toR = d => (d * Math.PI) / 180;
  const toD = r => (r * 180) / Math.PI;
  const φ1 = toR(lat1), λ1 = toR(lng1);
  const φ2 = toR(lat2), λ2 = toR(lng2);
  const d =
    2 * Math.asin(Math.sqrt(
      Math.sin((φ2 - φ1) / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2
    ));
  if (d < 0.0001) return [[lat1, lng1]];
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    pts.push([toD(Math.atan2(z, Math.sqrt(x * x + y * y))), toD(Math.atan2(y, x))]);
  }
  return pts;
}

let cssInjected = false;
function injectCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes routeDash { to { stroke-dashoffset: -40; } }
    .route-arc      { animation: routeDash 2.4s linear infinite; cursor: pointer; }
    .route-arc-fast { animation: routeDash 1.6s linear infinite; cursor: pointer; }
    .route-arc-slow { animation: routeDash 3.2s linear infinite; cursor: pointer; }
    .route-arc:hover, .route-arc-fast:hover, .route-arc-slow:hover { opacity: 1 !important; }

    /* ── Route map popup skin ── */
    .fly-route-popup .leaflet-popup-content-wrapper {
      background: #0f172a;
      border: 1px solid rgba(255,69,0,0.28);
      border-radius: 14px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
      padding: 0;
      overflow: hidden;
    }
    .fly-route-popup .leaflet-popup-tip-container { display: none; }
    .fly-route-popup .leaflet-popup-content { margin: 0; }
    .fly-route-popup .leaflet-popup-close-button {
      color: #94a3b8 !important; top: 7px !important; right: 9px !important;
      font-size: 16px !important;
    }
    .fly-rmap-popup {
      font-family: 'Space Grotesk', system-ui, sans-serif;
      padding: 14px 16px 12px;
      min-width: 172px;
    }
    .fly-rmap-popup-route {
      font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.3px;
    }
    .fly-rmap-popup-route span { color: #FF4500; margin: 0 3px; }
    .fly-rmap-popup-cities {
      font-size: 11px; color: #64748b; margin: 3px 0 5px;
    }
    .fly-rmap-popup-stats {
      font-size: 11px; color: #FF4500; margin-bottom: 10px; font-weight: 600;
    }
    .fly-rmap-popup-btn {
      display: inline-block; background: #FF4500; color: #fff;
      font-size: 11px; font-weight: 700; padding: 5px 13px;
      border-radius: 20px; border: none; cursor: pointer;
      transition: background 0.15s, transform 0.1s;
      font-family: inherit;
    }
    .fly-rmap-popup-btn:hover { background: #e03d00; transform: scale(1.03); }
    .fly-rmap-popup-secondary {
      display: inline-block; background: rgba(255,255,255,0.07); color: #94a3b8;
      font-size: 10px; font-weight: 600; padding: 4px 10px;
      border-radius: 20px; border: 1px solid rgba(255,255,255,0.1);
      cursor: pointer; margin-left: 5px; font-family: inherit;
    }
    .fly-rmap-popup-secondary:hover { background: rgba(255,255,255,0.13); color: #e2e8f0; }
    /* Airport dot hover scale */
    .route-apt-dot div { transition: transform 0.15s; cursor: pointer; }
    .route-apt-dot div:hover { transform: scale(1.7) !important; }
  `;
  document.head.appendChild(style);
}

export default function RouteMap({ routes = [], height = '520px', className = '' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const navigateRef = useRef(null);
  const [error, setError] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const navigate = useNavigate();

  // Keep navigate ref current
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    if (!routes.length || mapInstanceRef.current) return;
    let isMounted = true;

    const init = async () => {
      try {
        injectCSS();
        const L = (await import('leaflet')).default;
        await import('leaflet/dist/leaflet.css');

        if (!isMounted || !mapRef.current || mapInstanceRef.current) return;

        const map = L.map(mapRef.current, {
          center: [22, 15],
          zoom: 2,
          minZoom: 1,
          maxZoom: 7,
          scrollWheelZoom: true,
          zoomControl: true,
          attributionControl: false,
        });

        // Dark Carto tile
        L.tileLayer(
          'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
          { subdomains: 'abcd', maxZoom: 8 }
        ).addTo(map);

        L.control.attribution({ prefix: false }).addTo(map);
        map.attributionControl.addAttribution('© <a href="https://carto.com">CARTO</a>');

        // Sort routes: paint low-traffic arcs first so busiest ones are on top
        const sorted = [...routes].sort((a, b) => (a.checkin_count || 0) - (b.checkin_count || 0));
        const maxCheckins = Math.max(...sorted.map(r => r.checkin_count || 1), 1);

        const airportDots = new Map();

        sorted.forEach((route, idx) => {
          const { origin, destination, checkin_count, post_count, route_id } = route;
          if (!origin?.lat || !destination?.lat) return;

          const ratio = Math.min(1, (checkin_count || 1) / maxCheckins);
          const color  = ratio > 0.55 ? '#FF4500' : ratio > 0.3 ? '#ff8c00' : '#14b8a6';
          const opacity = 0.30 + ratio * 0.60;
          const weight  = 0.7 + ratio * 2.0;
          const speed   = ratio > 0.6 ? 'route-arc-fast' : ratio > 0.3 ? 'route-arc' : 'route-arc-slow';

          const pts = greatCirclePoints(origin.lat, origin.lng, destination.lat, destination.lng, 60);

          // Antimeridian split
          const segments = [];
          let seg = [pts[0]];
          for (let i = 1; i < pts.length; i++) {
            if (Math.abs(pts[i][1] - pts[i - 1][1]) > 180) { segments.push(seg); seg = [pts[i]]; }
            else seg.push(pts[i]);
          }
          segments.push(seg);

          // Rich route popup HTML
          const routePopupHtml = `
            <div class="fly-rmap-popup">
              <div class="fly-rmap-popup-route">${origin.code} <span>→</span> ${destination.code}</div>
              <div class="fly-rmap-popup-cities">${origin.city || origin.code} → ${destination.city || destination.code}</div>
              <div class="fly-rmap-popup-stats">✈ ${checkin_count || 0} travelers${post_count ? ` · ${post_count} posts` : ''}</div>
              <button class="fly-rmap-popup-btn" data-href="/route/${route_id}">View Community →</button>
            </div>`;

          segments.forEach(segment => {
            if (segment.length < 2) return;
            const polyline = L.polyline(segment, {
              color,
              weight,
              opacity,
              dashArray: '6 10',
              dashOffset: String(idx * 3),
              className: speed,
              interactive: true,
            });

            polyline.bindTooltip(
              `<strong>${origin.code} → ${destination.code}</strong><br>${origin.city} → ${destination.city}${post_count ? `<br><span style="color:#FF4500">✈ ${checkin_count} travelers</span>` : ''}`,
              { direction: 'top', sticky: true }
            );
            polyline.bindPopup(routePopupHtml, { className: 'fly-route-popup', maxWidth: 250, closeButton: true });
            polyline.on('click', (e) => { L.DomEvent.stopPropagation(e); polyline.openPopup(e.latlng); });

            polyline.on('mouseover', e => {
              polyline.setStyle({ weight: weight + 1.5, opacity: 1 });
              const pt = e.containerPoint;
              setTooltip({
                x: pt.x, y: pt.y,
                text: `${origin.code} → ${destination.code}`,
                sub: `${origin.city} to ${destination.city} · ${checkin_count} travelers`,
              });
            });
            polyline.on('mouseout', () => {
              polyline.setStyle({ weight, opacity });
              setTooltip(null);
            });

            polyline.addTo(map);
          });

          // Airport dots with popup
          [
            { apt: origin,      routeHref: `/route/${route_id}` },
            { apt: destination, routeHref: `/route/${route_id}` },
          ].forEach(({ apt }) => {
            const key = apt.code;
            if (airportDots.has(key)) return;
            airportDots.set(key, true);

            const dotSize = ratio > 0.5 ? 8 : 6;
            const dotIcon = L.divIcon({
              html: `<div style="width:${dotSize}px;height:${dotSize}px;background:${color};border:2px solid rgba(255,255,255,0.7);border-radius:50%;box-shadow:0 0 7px ${color}99;"></div>`,
              className: 'route-apt-dot',
              iconSize: [dotSize, dotSize],
              iconAnchor: [dotSize / 2, dotSize / 2],
            });

            const aptPopupHtml = `
              <div class="fly-rmap-popup">
                <div class="fly-rmap-popup-route">${apt.code}</div>
                <div class="fly-rmap-popup-cities">${apt.city || apt.code}${apt.country ? `, ${apt.country}` : ''}</div>
                <button class="fly-rmap-popup-btn" data-href="/airport/${apt.code}">View Airport →</button>
              </div>`;

            const marker = L.marker([apt.lat, apt.lng], { icon: dotIcon, interactive: true });
            marker.bindTooltip(`<strong>${apt.code}</strong> — ${apt.city || ''}`, { direction: 'top' });
            marker.bindPopup(aptPopupHtml, { className: 'fly-route-popup', maxWidth: 210, closeButton: true });
            marker.on('mouseover', e => {
              const pt = e.containerPoint;
              setTooltip({ x: pt.x, y: pt.y, text: apt.code, sub: apt.city });
            });
            marker.on('mouseout', () => setTooltip(null));
            marker.addTo(map);
          });
        });

        // Wire popup button clicks for SPA navigation
        map.on('popupopen', (e) => {
          const el = e.popup.getElement();
          if (!el) return;
          el.querySelectorAll('[data-href]').forEach(btn => {
            btn.onclick = (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              const href = btn.getAttribute('data-href');
              if (href && navigateRef.current) {
                map.closePopup();
                navigateRef.current(href);
              }
            };
          });
        });

        mapInstanceRef.current = map;
      } catch (err) {
        console.error('RouteMap init error:', err);
        if (isMounted) setError(true);
      }
    };

    init();
    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [routes]);

  if (error) {
    return (
      <div
        className={`rounded-2xl bg-muted flex items-center justify-center text-muted-foreground text-sm ${className}`}
        style={{ height }}
      >
        Map not available
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-border ${className}`}
      style={{ height, isolation: 'isolate' }}
    >

      {/* Hover tooltip overlay */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-[9999] bg-[#0f172a]/95 backdrop-blur-sm border border-[#FF4500]/20 rounded-xl px-3 py-2 shadow-xl"
          style={{ left: tooltip.x + 14, top: tooltip.y - 10, transform: 'translateY(-50%)' }}
        >
          <p className="font-semibold text-white text-sm">{tooltip.text}</p>
          {tooltip.sub && <p className="text-xs text-slate-400 mt-0.5">{tooltip.sub}</p>}
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[9000] bg-[#0f172a]/85 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-400 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="w-5 h-0.5 rounded-full bg-[#FF4500] inline-block" /> Busiest routes
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-5 h-0.5 rounded-full bg-[#14b8a6] inline-block" /> Active routes
        </div>
        <div className="mt-1 pt-1 border-t border-white/10 text-[10px] text-slate-500">
          Click any arc or dot for details
        </div>
      </div>
    </div>
  );
}
